import {
  type ComponentType,
  type ReactNode,
  type SVGProps,
  useState,
} from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  X,
} from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { normaliseDetailCardDisplayValue } from "./detail-card-formatting";

const DEFAULT_PREVIEW_COUNT = 6;

export const summaryDetailActionButtonClassName =
  "h-8 cursor-pointer rounded-[6px] border-none bg-[var(--green)] px-3.5 text-[11px] font-normal uppercase tracking-[0.09em] text-white hover:bg-[var(--green-hover)] disabled:cursor-not-allowed disabled:opacity-50";

export const summaryDetailAlertButtonClassName =
  "h-7 rounded-[6px] border-none bg-[var(--status-error)] px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-white hover:opacity-90";

export const summaryDetailAlertWarningButtonClassName =
  "h-7 rounded-[6px] border-none bg-[var(--status-warn)] px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-white hover:opacity-90";

// eslint-disable-next-line react-refresh/only-export-components
export function summaryDetailAlertButtonClassNameForLevel(
  level: SummaryDetailAlert["level"],
): string {
  switch (level) {
    case "warning":
      return summaryDetailAlertWarningButtonClassName;
    case "success":
      return "h-7 rounded-[6px] border-none bg-[var(--green)] px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-white hover:opacity-90";
    case "info":
      return "h-7 rounded-[6px] border-none bg-[var(--status-info)] px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-white hover:opacity-90";
    case "error":
    default:
      return summaryDetailAlertButtonClassName;
  }
}

export interface SummaryDetailItemData {
  key: string;
  label: string;
  value?: unknown;
  supportingText?: ReactNode;
  link?: boolean;
  onClick?: () => void;
  muted?: boolean;
  positive?: boolean;
  fullWidth?: boolean;
}

export interface SummaryDetailAlert {
  level: "info" | "success" | "warning" | "error";
  /** Bold subject line (e.g. "Warranty exception", "DRAFT"). */
  leadingText: ReactNode;
  /** Supporting copy under the subject. */
  message: ReactNode;
  /** Right-aligned action(s): primary CTA, optional secondary, etc. */
  action?: ReactNode;
  /** When provided, renders a circular dismiss control on the far right. */
  onDismiss?: () => void;
}

interface SummaryDetailCardProps {
  sectionLabel: string;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  showSectionLabel?: boolean;
  action?: ReactNode;
  /**
   * Optional overflow menu rendered to the right of the primary action.
   * Typically a {@link CardActionMenu} but accepts any node so callers can
   * compose the trigger themselves when needed.
   */
  menu?: ReactNode;
  alert?: SummaryDetailAlert;
  items?: SummaryDetailItemData[];
  body?: ReactNode;
  footer?: ReactNode;
  emptyState?: ReactNode;
  collapsible?: boolean;
  previewCount?: number;
  className?: string;
  gridClassName?: string;
}

export function SummaryDetailCard({
  sectionLabel,
  title,
  subtitle,
  icon,
  showSectionLabel = true,
  action,
  menu,
  alert,
  items = [],
  body,
  footer,
  emptyState,
  collapsible = false,
  previewCount = DEFAULT_PREVIEW_COUNT,
  className,
  gridClassName,
}: SummaryDetailCardProps) {
  const [expanded, setExpanded] = useState(false);
  const shouldCollapse = collapsible && items.length > previewCount;
  const visibleItems =
    shouldCollapse && !expanded ? items.slice(0, previewCount) : items;
  const hiddenCount = Math.max(items.length - previewCount, 0);
  const hasStandardGrid = visibleItems.length > 0;
  const hasFallbackBody = Boolean(body);
  const hasEmptyState =
    !hasStandardGrid && !hasFallbackBody && Boolean(emptyState);

  return (
    <section
      className={cn(
        "overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]",
        className,
      )}
    >
      {alert && (
        <div className="p-2">
          <SummaryCardAlertRow alert={alert} />
        </div>
      )}

      <div className="flex flex-col gap-5 p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            {showSectionLabel && (
              <div className="mb-3 flex min-w-0 items-center gap-2">
                <span className="truncate text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {sectionLabel}
                </span>
              </div>
            )}
            <div className="flex items-start gap-3 sm:gap-4">
              {icon && (
                <span className="mt-0.5 shrink-0 text-[var(--brand)] [&_svg]:h-[22px] [&_svg]:w-[22px] [&_svg]:[stroke-width:1.25] sm:[&_svg]:h-6 sm:[&_svg]:w-6">
                  {icon}
                </span>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-[17px] font-bold leading-[1.2] tracking-[-0.01em] text-[var(--ink)] sm:text-[19px] font-display !font-bold">
                  {title}
                </h3>
                {subtitle && (
                  <div className="mt-1 text-[13px] font-light leading-relaxed text-[var(--ink-2)]">
                    {subtitle}
                  </div>
                )}
              </div>
            </div>
          </div>
          {(action || menu) && (
            <div className="flex shrink-0 items-center gap-2">
              {action}
              {menu}
            </div>
          )}
        </div>

        {hasStandardGrid && (
          <SummaryDetailGrid className={gridClassName}>
            {visibleItems.map((item) => (
              <SummaryDetailItem key={item.key} {...item} />
            ))}
          </SummaryDetailGrid>
        )}

        {hasFallbackBody && body}

        {hasEmptyState && (
          <div className="text-[13px] font-light text-[var(--ink-3)]">
            {emptyState}
          </div>
        )}

        {footer && (
          <div className="border-t-[0.5px] border-t-[var(--rule)] pt-4">
            {footer}
          </div>
        )}

        {shouldCollapse && (
          <div className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <Button
              variant="ghost"
              onClick={() => setExpanded((value) => !value)}
              className="h-auto rounded-full px-0 py-0 text-[11px] font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink)]"
            >
              {expanded ? (
                <>
                  Show less
                  <ChevronUp className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  Show {hiddenCount} more
                  <ChevronDown className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function SummaryCardAlertRow({ alert }: { alert: SummaryDetailAlert }) {
  const tone = alertToneClasses(alert.level);
  const Icon = tone.Icon;
  return (
    <div
      role="alert"
      className={cn(
        "flex items-center gap-3 rounded-[8px] border-[0.5px] px-3 py-2.5 sm:px-3.5 sm:py-3",
        tone.pillBgClassName,
        tone.pillBorderClassName,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          tone.iconBgClassName,
        )}
      >
        <Icon
          className={cn("h-[18px] w-[18px]", tone.iconClassName)}
          strokeWidth={1.75}
        />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold leading-[1.2] tracking-[-0.005em] text-[var(--ink)]">
          {alert.leadingText}
        </div>
        {alert.message && (
          <div className="mt-0.5 text-[12px] font-light leading-[1.3] text-[var(--ink-2)]">
            {alert.message}
          </div>
        )}
      </div>
      {alert.action && (
        <div className="flex shrink-0 items-center gap-1.5">{alert.action}</div>
      )}
      {alert.onDismiss && (
        <button
          type="button"
          onClick={alert.onDismiss}
          aria-label="Dismiss"
          className="ml-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--rule)] bg-transparent text-[var(--ink-3)] transition-colors duration-150 hover:bg-[var(--bg-light)] hover:text-[var(--ink)]"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

function alertToneClasses(level: SummaryDetailAlert["level"]): {
  Icon: IconComponent;
  pillBgClassName: string;
  pillBorderClassName: string;
  iconBgClassName: string;
  iconClassName: string;
} {
  switch (level) {
    case "success":
      return {
        Icon: CheckCircle2,
        pillBgClassName: "bg-[var(--green-bg-subtle)]",
        pillBorderClassName: "border-[var(--rule)]",
        iconBgClassName: "bg-[var(--bg-card)]",
        iconClassName: "text-[var(--green)]",
      };
    case "warning":
      return {
        Icon: AlertTriangle,
        pillBgClassName:
          "bg-[color-mix(in_srgb,var(--status-warn-bg)_40%,var(--bg-subtle))]",
        pillBorderClassName: "border-[var(--rule)]",
        iconBgClassName: "bg-[var(--bg-card)]",
        iconClassName: "text-[var(--status-warn)]",
      };
    case "info":
      return {
        Icon: Info,
        pillBgClassName:
          "bg-[color-mix(in_srgb,var(--status-info-bg)_50%,var(--bg-subtle))]",
        pillBorderClassName: "border-[var(--rule)]",
        iconBgClassName: "bg-[var(--bg-card)]",
        iconClassName: "text-[var(--status-info)]",
      };
    case "error":
    default:
      return {
        Icon: AlertTriangle,
        pillBgClassName: "bg-[rgba(207,0,0,0.07)]",
        pillBorderClassName: "border-[var(--rule)]",
        iconBgClassName: "bg-[var(--bg-card)]",
        iconClassName: "text-[var(--status-error)]",
      };
  }
}

export function SummaryDetailGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-1 gap-x-10 gap-y-5 sm:grid-cols-2",
        "lg:grid-cols-3 lg:gap-x-6 lg:gap-y-4",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SummaryDetailItem({
  label,
  value,
  supportingText,
  link = false,
  onClick,
  muted = false,
  positive = false,
  fullWidth = false,
}: Omit<SummaryDetailItemData, "key">) {
  const displayValue = normaliseDetailCardDisplayValue(value) ?? "-";
  const isEmpty =
    value === null || value === undefined || value === "" || value === "-";

  const valueClassName = cn(
    "min-w-0 break-words text-[14px] font-light leading-[1.45]",
    link && onClick
      ? "text-[var(--green)]"
      : positive
        ? "text-[var(--green)]"
        : muted || isEmpty
          ? "text-[var(--ink-3)]"
          : "text-[var(--ink)]",
  );

  return (
    <div className={cn("min-w-0", fullWidth && "sm:col-span-2")}>
      <div className="mb-1 text-[11px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </div>
      {link && onClick ? (
        <Button
          variant="link"
          onClick={onClick}
          className="block h-auto w-full min-w-0 max-w-full justify-start whitespace-normal p-0 text-left text-[14px] font-light leading-[1.45] text-[var(--green)] [overflow-wrap:anywhere] hover:text-[var(--green-hover)]"
        >
          {displayValue}
        </Button>
      ) : (
        <div
          className={cn(valueClassName, "max-w-full [overflow-wrap:anywhere]")}
        >
          {displayValue}
        </div>
      )}
      {supportingText && (
        <div className="mt-0.5 text-[11px] font-light text-[var(--ink-3)]">
          {supportingText}
        </div>
      )}
    </div>
  );
}
